import BookviewUserPreferencesButton from "organisms/user-preferences/book-view/book-view-user-preferences-button";
import BookviewUserPreferencesPanel from "organisms/panels/bookview-tab-panels/bookview-user-preferences-panel";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import MobileSecondaryNavHeader from "./mobile-secondary-nav-header";
import Modal from "molecules/modals/modal";
import ModalCloseButton from "atoms/buttons/modal-close-button";
import React, { useState } from "react";
import ReferencePanel from "organisms/panels/bookview-tab-panels/reference-panel";
import ReferencePanelButton from "molecules/reference-panel/reference-panel-button";
import ShareSpacePanel from "molecules/share-space-panel/share-space-panel";
import ShareSpacePanelAtom from "utilities/atoms/bookview/share-space-panel-atom";
import ShareSpacePanelTab from "molecules/share-space-panel/share-space-panel-tab";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ModalTypes } from "molecules/constants/modal-types";
import { useAtomValue } from "jotai";
import { useLocalization } from "utilities/hooks/use-localization";
import { useMobileSecondaryNavContent } from "utilities/contexts/use-mobile-secondary-nav/use-mobile-secondary-nav-content";
import { useReferencePanel } from "utilities/hooks/reference-panel/use-reference-panel";
import BookviewSearchButton from "molecules/bookview-search/bookview-search-button";
import { useBookviewSearchContext } from "utilities/contexts/bookview-tab-panel/bookview-search-provider";
import MobileBookviewSearchHandle from "organisms/panels/bookview-tab-panels/bookview-search/mobile-bookview-search-handle";
import MobileBookviewSearchPanel from "organisms/panels/bookview-tab-panels/bookview-search/mobile-bookview-search-panel";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface MobileSecondaryNavProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "c-mobile-secondary-nav";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const MobileSecondaryNav: React.FC<MobileSecondaryNavProps> = (
    props: MobileSecondaryNavProps
) => {
    const { t } = useLocalization();
    const [isOpen, setIsOpen] = useState(false);
    const { isSearchCollapsed, setIsSearchCollapsed } =
        useBookviewSearchContext();
    const { allowShareMultiple } = useFeatureFlags();

    const {
        toggleSecondaryNavModal,
        isRefModalOpen,
        isUserPreferenceModalOpen,
        isShareSpaceModalOpen,
        isSecondaryNavModalOpen,
        isBookviewSearchPanelOpen,
    } = useMobileSecondaryNavContent();

    const shareSpacePanel = useAtomValue(ShareSpacePanelAtom);

    const numberOfShareItems = shareSpacePanel.getShareItemsLength();

    const { userReferences } = useReferencePanel();
    const userReferencesCount = userReferences.count();

    const toggleMobileSecondaryNavModal = () => {
        toggleSecondaryNavModal();
        setIsOpen(true);
    };

    const handleToggleSearchPanel = () => setIsSearchCollapsed((prev) => !prev);

    const modalClassName = `${CSS_CLASS_NAME}__modal${isBookviewSearchPanelOpen ? " -search-open" : ""}${isSearchCollapsed ? " -collapsed" : ""}`;

    return (
        <>
            <Button
                onClick={toggleMobileSecondaryNavModal}
                accessibleText={t("secondary-nav-button")}
                style={ButtonStyles.None}
                size={ButtonSizes.Medium}
                cssClassName={`${CSS_CLASS_NAME}__menu-button`}
                disabled={isOpen}>
                <Icon size={IconSizes.Large} type={Icons.MoreVertical} />
            </Button>
            <Modal
                type={ModalTypes.Bottom}
                closeButtonStyle={ModalCloseButtonStyle.Hidden}
                label={"Bookview Secondary Navigation Modal"}
                closeDialog={() => setIsOpen(false)}
                isVisible={isOpen}
                displayOverlay={!isSearchCollapsed}
                bypassScrollLock={isSearchCollapsed}
                cssClassName={modalClassName}>
                {isBookviewSearchPanelOpen && (
                    <MobileBookviewSearchHandle
                        handleToggle={handleToggleSearchPanel}
                    />
                )}

                <MobileSecondaryNavHeader
                    numberOfShareItems={numberOfShareItems}
                    userReferencesCount={userReferencesCount}
                    closeModal={() => {
                        setIsOpen(false);
                        setIsSearchCollapsed(false);
                    }}
                />

                <div
                    className="-collapsible"
                    data-collapsed={isSearchCollapsed}>
                    {isSecondaryNavModalOpen && (
                        <div className={`${CSS_CLASS_NAME}__menu`}>
                            <div
                                className={`${CSS_CLASS_NAME}__menu-item-wrapper`}>
                                <BookviewSearchButton />
                            </div>
                            {allowShareMultiple && (
                                <div
                                    className={`${CSS_CLASS_NAME}__menu-item-wrapper`}>
                                    <ShareSpacePanelTab />
                                </div>
                            )}
                            <div
                                className={`${CSS_CLASS_NAME}__menu-item-wrapper`}>
                                <ReferencePanelButton />
                            </div>
                            <div
                                className={`${CSS_CLASS_NAME}__menu-item-wrapper`}>
                                <BookviewUserPreferencesButton />
                            </div>
                            <ModalCloseButton
                                cssClassName={`${CSS_CLASS_NAME}__close-button`}
                                modalType={ModalTypes.Bottom}
                                onClick={() => setIsOpen(false)}
                            />
                        </div>
                    )}
                    {isShareSpaceModalOpen && (
                        <ShareSpacePanel>
                            <ModalCloseButton
                                cssClassName={`${CSS_CLASS_NAME}__close-button-share`}
                                modalType={ModalTypes.Bottom}
                                onClick={() => setIsOpen(false)}
                            />
                        </ShareSpacePanel>
                    )}
                    {isRefModalOpen && (
                        <ReferencePanel handleCloseModal={setIsOpen}>
                            <ModalCloseButton
                                cssClassName={`${CSS_CLASS_NAME}__close-button`}
                                modalType={ModalTypes.Bottom}
                                onClick={() => setIsOpen(false)}
                            />
                        </ReferencePanel>
                    )}
                    {isUserPreferenceModalOpen && (
                        <div className={`${CSS_CLASS_NAME}__content`}>
                            <BookviewUserPreferencesPanel>
                                <ModalCloseButton
                                    cssClassName={`${CSS_CLASS_NAME}__close-button-preferences`}
                                    modalType={ModalTypes.Bottom}
                                    onClick={() => setIsOpen(false)}
                                />
                            </BookviewUserPreferencesPanel>
                        </div>
                    )}
                    {isBookviewSearchPanelOpen && (
                        <MobileBookviewSearchPanel>
                            <div
                                className={`${CSS_CLASS_NAME}__close-button-search`}>
                                <ModalCloseButton
                                    modalType={ModalTypes.Bottom}
                                    onClick={() => setIsOpen(false)}
                                />
                            </div>
                        </MobileBookviewSearchPanel>
                    )}
                </div>
            </Modal>
        </>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default MobileSecondaryNav;

// #endregion Exports
